// ============================================================
// pages/LoyaltyPage.jsx — Connected to Firebase
// ============================================================

import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLoyalty } from "../hooks/useFirestore";

export default function LoyaltyPage() {
  const { profile } = useAuth();
  const { config, transactions, loading } = useLoyalty();

  const points = profile?.loyaltyPoints || 0;
  const tier = profile?.loyaltyTier || "bronze";
  const totalOrders = profile?.totalOrders || 0;
  const totalSpent = profile?.totalSpent || 0;
  const memberSince = profile?.createdAt?.toDate
    ? profile.createdAt.toDate().toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "—";

  // Calculate progress to next tier
  const tiers = config?.tiers || [
    { name: "Bronze", minPoints: 0, discountPercent: 5 },
    { name: "Silver", minPoints: 500, discountPercent: 8 },
    { name: "Gold", minPoints: 1000, discountPercent: 10 },
    { name: "Platinum", minPoints: 2000, discountPercent: 15 },
  ];

  const currentTierIdx = tiers.findIndex((t) => t.name.toLowerCase() === tier);
  const nextTier = tiers[currentTierIdx + 1];
  const progressPercent = nextTier
    ? Math.min(100, Math.round((points / nextTier.minPoints) * 100))
    : 100;

  const tierColors = {
    bronze: "#CD7F32",
    silver: "#A0A0A0",
    gold: "#F5A623",
    platinum: "#8B5CF6",
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Loyalty Rewards</h2>
          <p>Earn points with every purchase and unlock sweet rewards</p>
        </div>
      </div>

      {/* Points Card */}
      <div className="loyalty-card" style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.78rem", opacity: 0.7, textTransform: "uppercase", letterSpacing: 1 }}>Your Points</div>
            <h3 style={{ fontSize: "2.5rem" }}>{points.toLocaleString()}</h3>
            <div style={{ fontSize: "0.88rem", opacity: 0.85, marginTop: 4 }}>
              <span style={{
                display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 12,
                background: "rgba(255,255,255,0.15)", fontWeight: 600, textTransform: "capitalize",
              }}>
                {tier} Tier
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.78rem", opacity: 0.7 }}>Member since {memberSince}</div>
            <div style={{ fontSize: "0.88rem", marginTop: 4 }}>{totalOrders} orders placed</div>
            <div style={{ fontSize: "0.88rem" }}>Rs {totalSpent.toLocaleString()} total spent</div>
          </div>
        </div>
        {nextTier && (
          <>
            <div className="loyalty-progress">
              <div className="loyalty-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", opacity: 0.8 }}>
              <span>{points} pts</span>
              <span>{nextTier.minPoints} pts — {nextTier.name} Tier!</span>
            </div>
          </>
        )}
        {!nextTier && (
          <div style={{ marginTop: "1rem", fontSize: "0.9rem", opacity: 0.9 }}>
            🎉 You've reached the highest tier! Enjoy all exclusive rewards.
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="section-header"><h2 style={{ fontSize: "1.4rem" }}>How It Works</h2></div>
      <div className="features-grid">
        {[
          { icon: "🛒", title: "Earn Points", desc: `Get ${config?.pointsPerAmount || 1} point for every Rs ${config?.amountThreshold || 10} spent on any product.${config?.doublePointsDays?.length ? ` 2× points on ${config.doublePointsDays.join(", ")}!` : ""}` },
          { icon: "🎁", title: "Unlock Rewards", desc: "Move through tiers to unlock bigger discounts, free products, and VIP perks." },
          { icon: "⭐", title: "VIP Benefits", desc: "Top-tier customers get early access to new products, birthday bonuses & exclusive tastings." },
        ].map((f, i) => (
          <div key={i} className="feature-card">
            <div className="feature-icon" style={{ fontSize: "1.5rem" }}>{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        ))}
      </div>

      <div className="divider" />

      {/* Tiers */}
      <div className="section-header"><h2 style={{ fontSize: "1.4rem" }}>Reward Tiers</h2></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
        {tiers.map((t, i) => {
          const isActive = t.name.toLowerCase() === tier;
          const color = tierColors[t.name.toLowerCase()] || "var(--chai)";
          return (
            <div key={i} className="card" style={{
              borderTop: `4px solid ${color}`,
              textAlign: "center",
              background: isActive ? "rgba(196,136,58,0.05)" : undefined,
              position: "relative",
            }}>
              {isActive && (
                <div style={{
                  position: "absolute", top: 8, right: 8, background: color, color: "white",
                  fontSize: "0.68rem", padding: "0.15rem 0.5rem", borderRadius: 8, fontWeight: 700,
                }}>
                  Current
                </div>
              )}
              <h3 style={{ fontFamily: "'Playfair Display', serif", color }}>{t.name}</h3>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, margin: "0.4rem 0" }}>
                {t.minPoints}+ pts
              </div>
              <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--espresso)" }}>
                {t.discountPercent}% discount
              </div>
              {t.rewards && (
                <div style={{ marginTop: "0.5rem" }}>
                  {(t.rewards || []).map((r, j) => (
                    <div key={j} style={{ fontSize: "0.82rem", color: "var(--cocoa)" }}>{r}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Transaction History */}
      {transactions.length > 0 && (
        <>
          <div className="divider" />
          <div className="section-header"><h2 style={{ fontSize: "1.4rem" }}>Points History</h2></div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>Reason</th><th>Points</th><th>Balance</th></tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td style={{ fontSize: "0.85rem" }}>{formatDate(tx.createdAt)}</td>
                    <td>
                      <span className={`inline-tag`} style={{
                        background: tx.type === "earned" ? "rgba(156,175,136,0.2)" : tx.type === "bonus" ? "rgba(245,166,35,0.15)" : "rgba(231,76,60,0.1)",
                        color: tx.type === "earned" ? "var(--sage-dark)" : tx.type === "bonus" ? "#F5A623" : "#e74c3c",
                      }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{tx.reason}</td>
                    <td style={{ fontWeight: 700, color: tx.points > 0 ? "var(--sage-dark)" : "#e74c3c" }}>
                      {tx.points > 0 ? "+" : ""}{tx.points}
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{tx.balanceAfter} pts</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
